/** Point-of-interest presentation + light gameplay hooks. */

export type PoiKind = "scrap_field" | "relay" | "ambush" | "cache" | "boss_arena";

/** Top-down marker glyph — shape identity, not color-only. */
export type PoiMarkerShape = "diamond" | "mast" | "triangle" | "crate" | "star";

export type PoiDef = {
  kind: PoiKind;
  /** Plain-language world/HUD name. */
  name: string;
  /** One-line tip when the player enters the radius. */
  tip: string;
  /** Ring + accent color (industrial grit palette). */
  color: number;
  /** Secondary accent for inner ring / beacon. */
  accent: number;
  marker: PoiMarkerShape;
  /** Default procedural radius in meters. */
  radius: number;
};

export const POI_DEFS: Record<PoiKind, PoiDef> = {
  scrap_field: {
    kind: "scrap_field",
    name: "Scrap Field",
    tip: "Scrap Field — salvage denser here. Scoop the piles.",
    color: 0xb08a3a,
    accent: 0xd4b06a,
    marker: "diamond",
    radius: 14,
  },
  relay: {
    kind: "relay",
    name: "Relay",
    tip: "Relay — short threat lull. Catch your breath.",
    color: 0x5a8aaa,
    accent: 0x8ec4d8,
    marker: "mast",
    radius: 14,
  },
  ambush: {
    kind: "ambush",
    name: "Ambush Zone",
    tip: "Ambush Zone — hostiles inbound. Break contact or fight.",
    color: 0xb05a3a,
    accent: 0xe08050,
    marker: "triangle",
    radius: 20,
  },
  cache: {
    kind: "cache",
    name: "Cache",
    tip: "Cache — sealed stash pinged. Grab the drop.",
    color: 0x6a8a52,
    accent: 0xa0c070,
    marker: "crate",
    radius: 14,
  },
  boss_arena: {
    kind: "boss_arena",
    name: "Boss Arena",
    tip: "BOSS ARENA — defeat the arena boss for XP and salvage.",
    color: 0xc45c26,
    accent: 0xff8a4a,
    marker: "star",
    radius: 28,
  },
};

/** Presentation + encounter copy for boss arenas. */
export const ARENA_GUARDIAN_LABEL = "ARENA GUARDIAN";
export const BOSS_ARENA_BANNER = "BOSS ARENA";
export const BOSS_ARENA_OBJECTIVE = "Defeat the Arena Guardian";
export const BOSS_ARENA_CLEAR_BANNER = "ARENA CLEARED";
export const SIEGE_WARDEN_BANNER = "SIEGE WARDEN";
export const SIEGE_WARDEN_OBJECTIVE = "Break the Siege Warden";
export const SIEGE_WARDEN_CLEAR_BANNER = "WARDEN BROKEN";
export const DREAD_HARVESTER_BANNER = "DREAD HARVESTER";
export const DREAD_HARVESTER_OBJECTIVE = "Break the Harvester and reclaim its cargo";
export const DREAD_HARVESTER_CLEAR_BANNER = "HARVESTER SCRAPPED";
export const VELOCITY_SOVEREIGN_BANNER = "VELOCITY SOVEREIGN";
export const VELOCITY_SOVEREIGN_OBJECTIVE = "Break the Sovereign and its Aegis screen";
export const VELOCITY_SOVEREIGN_CLEAR_BANNER = "SOVEREIGN BROKEN";
export const FOUNDRY_LEVIATHAN_BANNER = "FOUNDRY LEVIATHAN";
export const FOUNDRY_LEVIATHAN_OBJECTIVE = "Break the foundry and recover its boarded cargo";
export const FOUNDRY_LEVIATHAN_CLEAR_BANNER = "FOUNDRY SILENCED";
export const NULL_CROWN_BANNER = "NULL CROWN";
export const NULL_CROWN_OBJECTIVE = "Break the Nullifier lattice, then shatter the Crown";
export const NULL_CROWN_CLEAR_BANNER = "CROWN SHATTERED";
export const CENTURION_BANNER = "CENTURION BASTION";
export const CENTURION_OBJECTIVE = "Survive each counterpulse and break the Bastion";
export const CENTURION_CLEAR_BANNER = "CENTURION FALLEN";
export const BOSS_ARENA_HINT_RANGE_M = 120;

export const POI_KINDS: PoiKind[] = ["scrap_field", "relay", "ambush", "cache", "boss_arena"];

export function poiDef(kind: PoiKind): PoiDef {
  return POI_DEFS[kind];
}

export function poiDisplayName(kind: PoiKind): string {
  return POI_DEFS[kind].name;
}
